/**
 * What a UK postcode looks like, and the one spelling we store it in.
 *
 * 🚨 Directive-free on purpose, like `source.ts` next door. Four places need
 * this rule — the geocoder (server-only), the profile route, the team route,
 * and the setup wizard, which validates as somebody types and therefore runs
 * in a browser. A shared pure helper that lives in a client file is a throwing
 * proxy on the server, and one that lives in a `server-only` file cannot be
 * imported by the browser at all, so it belongs in neither (CLAUDE.md #18).
 *
 * It is also one rule and not four. Writing this regex out at each call site
 * is anti-pattern #20 in miniature: they start identical, one of them gets a
 * fix, and the field that accepted a postcode on one screen rejects it on the
 * next.
 */

/**
 * Uppercase, all whitespace stripped — the form `postcode_locations` is keyed
 * on, and therefore the form every read and every write must use. A row
 * written as "rg1 1aa" and looked up as "RG11AA" would never meet again.
 */
export function normalisePostcode(raw: string | null | undefined): string {
  return (raw ?? '').replace(/\s+/g, '').toUpperCase();
}

/**
 * Does this look like a full UK postcode?
 *
 * Deliberately a SHAPE test and not an existence test. Whether a postcode is
 * real is postcodes.io's question, it answers for free, and it is the only one
 * that can — a brand-new estate has postcodes the ONS file has not caught up
 * with, and refusing to let an owner record one would be worse than measuring
 * that engineer's first drive as unknown.
 *
 * Its job is only to keep `N/A`, `TBC`, `ask Dave` and the empty string out of
 * a batch request, which real job records are full of.
 */
export function looksLikeAPostcode(normalised: string): boolean {
  return /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(normalised);
}

/** Normalise and shape-check in one, for a field somebody is typing into. */
export function readPostcode(raw: string | null | undefined): { ok: true; postcode: string } | { ok: false } {
  const normalised = normalisePostcode(raw);
  return looksLikeAPostcode(normalised) ? { ok: true, postcode: normalised } : { ok: false };
}

/**
 * The last full UK postcode written anywhere in a piece of free text, in its
 * spaced form ("SW1A 1AA"), or null when there is none.
 */
export function postcodeIn(text: string | null | undefined): string | null {
  const matches = [...(text ?? '').matchAll(/\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/gi)];
  const last = matches.at(-1);
  return last ? `${last[1]} ${last[2]}`.toUpperCase() : null;
}

const POSTCODE_ANYWHERE = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/gi;
/** "12", "12A", "1-3": a house number and nothing else. */
const HOUSE_NUMBER = /^\d+[A-Z]?(?:\s*[-–]\s*\d+[A-Z]?)?$/i;
/** "12 High Street", "12A Mill Lane", "1-3 Bridge St": a number, then words. */
const NUMBERED_STREET = /^\d+[A-Z]?(?:\s*[-–]\s*\d+[A-Z]?)?\s+[A-Z]/i;

/**
 * The first "number + street" in a comma-joined address, or null. Also joins
 * the number back to its street when they were saved on two lines ("12" /
 * "High Street", about one property in nine on production). Anything before
 * it — a property label, a house name, "Flat 2" — is skipped.
 */
function numberedStreetIn(text: string): string | null {
  const parts = text.replace(POSTCODE_ANYWHERE, '').split(',').map(p => p.trim()).filter(Boolean);
  for (let i = 0; i < parts.length; i++) {
    if (NUMBERED_STREET.test(parts[i])) return parts[i];
    if (HOUSE_NUMBER.test(parts[i]) && /^[A-Z]/i.test(parts[i + 1] ?? '')) return `${parts[i]} ${parts[i + 1]}`;
  }
  return null;
}

/**
 * What to hand a maps app for an address: "number street, postcode" when it
 * has both, the postcode alone when it has no numbered street, and the text as
 * written when it has no postcode at all.
 *
 * 🚨 The whole address is NOT a safer query, it is a worse one. A maps app's
 * search treats every word as a hint and is free to let any of them outvote
 * the postcode. Measured in Google Maps on 2026-10-15, for a plumbing firm's
 * engineers who were being sent to the wrong houses:
 *
 *   • A HOUSE NAME is matched to any building of that name, postcode or not.
 *     A rural farm's name plus its own postcode went to a same-named farm in
 *     the next valley, under a different postcode. So a house name is never
 *     sent: with no numbered street, the postcode goes alone.
 *   • TOWN AND COUNTY tip a street Google barely knows (a new-build estate)
 *     onto one it knows well: "6 <X> Way, <town>, <county>, <postcode>" went
 *     to "6 <X> St" across town, while "6 <X> Way, <postcode>" landed on
 *     <X> Way. So the town and county are never sent either.
 *   • "Number street, postcode" found the exact house on six of six randomly
 *     sampled production addresses, which the postcode alone cannot: it lands
 *     on a street and a handful of doors.
 *
 * Where somebody has pinned the door (TKT-1445) the pin still wins over all of
 * it.
 */
export function navigationQuery(text: string): string {
  const postcode = postcodeIn(text);
  if (!postcode) return text.trim();
  const street = numberedStreetIn(text);
  return street ? `${street}, ${postcode}` : postcode;
}
